import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { ApiError } from "@shared-core/data/api/errors";
import { extractFieldErrors } from "@shared-core/data/validation";
import { ErrorPanel } from "../../app/components/patterns/ErrorPanel";

/** One tab's read: loading, failed (with a retry), or loaded. */
export type Loaded<T> =
  | { status: "loading" }
  | { status: "failed"; message: string }
  | { status: "loaded"; data: T };

/** Load on mount and when `load` changes; `reload` re-reads (after a write). */
export function useLoaded<T>(load: () => Promise<T>): [Loaded<T>, () => void] {
  const [state, setState] = useState<Loaded<T>>({ status: "loading" });
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    let cancelled = false;
    load()
      .then((data) => !cancelled && setState({ status: "loaded", data }))
      .catch(
        (err: unknown) =>
          !cancelled &&
          setState({ status: "failed", message: describeError(err) }),
      );
    return () => {
      cancelled = true;
    };
  }, [load, generation]);
  const reload = useCallback(() => setGeneration((g) => g + 1), []);
  return [state, reload];
}

/** The server's own words for a failure: a field-tagged 400's messages, a detail string, or a generic line. */
export function describeError(err: unknown): string {
  const fieldErrors = extractFieldErrors(err);
  if (fieldErrors.length) return fieldErrors.map((e) => e.message).join(" ");
  if (err instanceof ApiError) {
    if (err.status === 404) return "Not available in this preview.";
    if (err.detail) return err.detail;
  }
  return "Something went wrong talking to the preview's API.";
}

export function LoadingRow({ label }: { label: string }) {
  return (
    <div
      className="flex items-center gap-2 py-12 justify-center text-muted-foreground"
      role="status"
    >
      <Loader2 className="size-5 animate-spin" />
      {label}
    </div>
  );
}

export function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground">
      {children}
    </div>
  );
}

export function FailedRow({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return <ErrorPanel message={message} onRetry={onRetry} />;
}
